import React, { useState } from "react";
import allImages from "../../assets/images-import.jsx";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { login, storeAuthCookies } from "../../services/authService";
import { handleErrors } from "../../utils/errorHandler";
import { Helmet } from "react-helmet-async";
import "../../theme/xcruit-auth.css";

const FEATURE_POINTS = [
  {
    icon: "ri-article-line",
    title: "Published Articles",
    desc: "Manage and publish energy intelligence content",
  },
  {
    icon: "ri-draft-line",
    title: "Draft Articles",
    desc: "Save work-in-progress drafts before going live",
  },
  {
    icon: "ri-mail-line",
    title: "Email Subscribers",
    desc: "Grow and manage your subscriber list",
  },
  {
    icon: "ri-advertisement-line",
    title: "Advertisements",
    desc: "Control banners and promotional placements",
  },
];

export const SignInContent = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [keepLogged, setKeepLogged] = useState(false);
  const [error, setError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const redirectAfterLogin = localStorage.getItem("redirectAfterLogin");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Please enter both username and password");
      return;
    }
    setError("");

    const loginData = {
      userName: username,
      password: password,
      keepLogged: keepLogged ? "true" : "false",
    };

    try {
      setIsButtonDisabled(true);
      const response = await login(loginData);
      const { data } = response;

      if (!data?.isSuccess) {
        toast.error(data?.message || "Login failed. Please try again.");
        setError(
          data?.message ||
            "Login failed. Please check your credentials and try again."
        );
        return;
      }

      const result = data.result;

      if (!result?.accessToken || !result?.refreshToken) {
        toast.error("Login failed. Invalid response from server.");
        setError("Login failed. Please check your credentials and try again.");
        return;
      }

      storeAuthCookies(result, keepLogged);
      toast.success(data.message || "Login successful!");

      if (redirectAfterLogin) {
        navigate(redirectAfterLogin);
        localStorage.removeItem("redirectAfterLogin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      handleErrors(error);
      setError("Login failed. Please check your credentials and try again.");
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prevState) => !prevState);
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />

      <div className="xcruit-auth-page">
        <div className="xcruit-auth-bg">
          <img src={allImages.loginPeople} alt="" />
        </div>
        <div className="xcruit-auth-blob xcruit-auth-blob--1" />
        <div className="xcruit-auth-blob xcruit-auth-blob--2" />
        <div className="xcruit-auth-blob xcruit-auth-blob--3" />

        <div className="xcruit-auth-shell">
          {/* Left — Brand + Feature Points */}
          <div className="xcruit-auth-left">
            <div className="xcruit-auth-left-logo">
              <img src={allImages.logoDark} alt="Xcruit Energy" />
            </div>
            <h2 className="xcruit-auth-left-title">
              Energy Intelligence Admin
            </h2>
            <p className="xcruit-auth-left-desc">
              Manage articles, subscribers, and advertisements from one
              powerful dashboard.
            </p>

            <div className="xcruit-auth-points">
              {FEATURE_POINTS.map((point, index) => (
                <div
                  className="xcruit-auth-point"
                  key={point.title}
                  style={{ animationDelay: `${0.35 + index * 0.12}s` }}
                >
                  <span className="xcruit-auth-point-icon">
                    <i className={point.icon}></i>
                  </span>
                  <div className="xcruit-auth-point-text">
                    <strong>{point.title}</strong>
                    <span>{point.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Login Card */}
          <div className="xcruit-auth-right">
            <div className="xcruit-auth-card">
              <div className="xcruit-auth-logo auth-logo-mobile">
                <img src={allImages.logoDark} alt="Xcruit Energy" />
              </div>

              <h1 className="xcruit-auth-heading">Welcome Back</h1>
              <p className="xcruit-auth-subtext">
                Sign in to your admin dashboard
              </p>
              <div className="xcruit-auth-divider" />

              <form onSubmit={handleSubmit} className="xcruit-auth-form">
                {error && <div className="xcruit-auth-alert">{error}</div>}

                <div className="form-group">
                  <label className="form-label" htmlFor="txtUserName">
                    Username
                  </label>
                  <div className="input-group-icon">
                    <i className="mdi mdi-account-outline input-icon"></i>
                    <input
                      type="text"
                      className="form-control"
                      id="txtUserName"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="txtPassword">
                    Password
                  </label>
                  <div className="input-group-icon">
                    <i className="mdi mdi-lock-outline input-icon"></i>
                    <input
                      type={isPasswordVisible ? "text" : "password"}
                      className="form-control"
                      id="txtPassword"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      tabIndex={-1}
                    >
                      <i
                        className={
                          isPasswordVisible
                            ? "mdi mdi-eye-off-outline"
                            : "mdi mdi-eye-outline"
                        }
                      ></i>
                    </button>
                  </div>
                </div>

                <div className="xcruit-auth-meta">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={keepLogged}
                      onChange={(e) => setKeepLogged(e.target.checked)}
                      id="chkLogKeep"
                    />
                    <label className="form-check-label" htmlFor="chkLogKeep">
                      Remember me
                    </label>
                  </div>
                  <Link to="/forgot-password">Forgot password?</Link>
                </div>

                <button
                  type="submit"
                  className="btn-signin"
                  disabled={isButtonDisabled}
                >
                  {isButtonDisabled ? (
                    "Signing In..."
                  ) : (
                    <>
                      Sign In
                      <span className="btn-arrow">&#8594;</span>
                    </>
                  )}
                </button>
              </form>

              <div className="xcruit-auth-footer">
                &copy; {new Date().getFullYear()} Xcruit Energy. All rights
                reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
